import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiUrlKey } from '../utils/api-resource.utils';
import { XCustomErrorMessage, XTransactionName, XTransactionTime } from '../interceptors/http/http.interceptor';

/**
 * Type definition for optional query parameters
 */
export interface QueryParams {
  [key: string]: unknown;
}

/**
 * Service for unified communication with the backend API.
 * Supports all common HTTP methods and automatically sets withCredentials to true.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(apiUrlKey) private readonly apiUrl: string
  ) {}

  /**
   * Sends a GET request to the specified API resource.
   *
   * @template T - Expected return type
   * @param resource - Relative path to the API resource
   * @param transaction - Optional transaction name for logging
   * @param customErrorMessage - Optional custom error message
   * @returns An `Observable<T>` with the server response
   */
  get<T>(resource: string, transaction?: string, customErrorMessage?: string): Observable<T> {
    const url = `/api/${resource}`;
    const context = this.buildHttpContext(transaction, customErrorMessage);
    return this.httpClient.get<T>(this.buildUrl(url), {
      ...context,
      responseType: 'json',
    });
  }

  /**
   * Sends a PUT request to update resources.
   *
   * @template T - Type of the body to be sent
   * @param resource - Relative path to the API resource
   * @param body - Data object for the update
   * @param transaction - Optional transaction name
   * @param customErrorMessage - Optional custom error message
   * @returns An `Observable<any>` with the server response
   */
  put<T, U>(resource: string, body: T, transaction?: string, customErrorMessage?: string): Observable<U> {
    const url = `/api/${resource}`;
    const context = this.buildHttpContext(transaction, customErrorMessage);
    return this.httpClient.put<U>(this.buildUrl(url), body, {
      ...context,
      responseType: 'json',
    });
  }

  /**
   * Sends a POST request to create new resources.
   *
   * @template T - Expected return type
   * @template B - Type of the request body
   * @param resource - Relative path to the API resource
   * @param body - Optional data object for creation
   * @param transaction - Optional transaction name
   * @param customErrorMessage - Optional custom error message
   * @returns An `Observable<T>` with the server response
   */
  post<T, B = unknown>(
    resource: string,
    body?: B,
    transaction?: string,
    customErrorMessage?: string,
    additionalHeaders?:
      | HttpHeaders
      | {
          [header: string]: string | string[];
        }
  ): Observable<T> {
    const url = `/api/${resource}`;
    const context = this.buildHttpContext(transaction, customErrorMessage, additionalHeaders);
    return this.httpClient.post<T>(this.buildUrl(url), body, {
      ...context,
      responseType: 'json',
    });
  }

  /**
   * Sends a PATCH request to partially update a resource.
   *
   * @template T - Expected return type
   * @template B - Type of the request body
   * @param args - Object containing `resource`, `body`, and optional `transaction`
   * @returns An `Observable<T>` with the server response
   */
  patch<T, B = unknown>({
    resource,
    body,
    transaction,
    customErrorMessage,
  }: {
    resource: string;
    body: B;
    transaction?: string;
    customErrorMessage?: string;
  }): Observable<T> {
    const url = `/api/${resource}`;
    const context = this.buildHttpContext(transaction, customErrorMessage);
    return this.httpClient.patch<T>(this.buildUrl(url), body, {
      ...context,
      responseType: 'json',
    });
  }

  /**
   * Sends a DELETE request to delete a resource.
   *
   * @template T - Expected return type
   * @param resource - Relative path to the API resource
   * @param body - Optional body to send additional information
   * @param transaction - Optional transaction name
   * @param customErrorMessage - Optional custom error message
   * @returns An `Observable<T>` with the server response
   */
  delete<T>(resource: string, body?: T, transaction?: string, customErrorMessage?: string): Observable<string> {
    const url = `/api/${resource}`;
    const context = this.buildHttpContext(transaction, customErrorMessage);
    return this.httpClient.delete<string>(this.buildUrl(url), {
      ...context,
      responseType: 'json',
      body,
    });
  }

  /**
   * Creates the common HTTP context including headers and optional transaction context.
   *
   * @param transaction - Optional transaction name
   * @param customErrorMessage - Optional custom error message
   * @returns An object with `headers`, `context`, and `withCredentials`
   */
  private buildHttpContext(
    transaction?: string,
    customErrorMessage?: string,
    additionalHeaders?: HttpHeaders | { [header: string]: string | string[] }
  ): {
    headers: HttpHeaders;
    context?: HttpContext;
    withCredentials: boolean;
  } {
    // Start with base headers
    let headers = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    // Merge additional headers if provided
    if (additionalHeaders) {
      if (additionalHeaders instanceof HttpHeaders) {
        additionalHeaders.keys().forEach((key) => {
          const value = additionalHeaders.getAll(key);
          if (value) {
            headers = headers.set(key, value);
          }
        });
      } else {
        Object.entries(additionalHeaders).forEach(([key, value]) => {
          headers = headers.set(key, value);
        });
      }
    }

    // Build context
    let context = new HttpContext();
    if (transaction) {
      context = context.set(XTransactionName, transaction).set(XTransactionTime, Date.now().toString());
    }
    if (customErrorMessage) {
      context = context.set(XCustomErrorMessage, customErrorMessage);
    }

    return {
      headers,
      context,
      withCredentials: true,
    };
  }

  /**
   * Builds the full URL to the specified resource.
   *
   * @param resource - Relative API path (e.g., `/api/users`)
   * @returns Full URL as a `string`
   */
  private buildUrl(resource: string): string {
    return `${this.apiUrl}${resource}`;
  }
}
